// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { Component, inject, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { firstValueFrom } from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { FeatureFlag } from "@bitwarden/common/enums/feature-flag.enum";
import { UpdateDomainsRequest } from "@bitwarden/common/models/request/update-domains.request";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { DialogService, PopoverModule } from "@bitwarden/components";

import { HeaderModule } from "../layouts/header/header.module";
import { SharedModule } from "../shared";

import {
  AddCustomDomainDialogComponent,
  AddCustomDomainDialogResult,
} from "./add-custom-domain-dialog.component";
import {
  CustomizeGlobalDomainDialogComponent,
  CustomizeGlobalDomainDialogResult,
} from "./customize-global-domain-dialog.component";

type GlobalEquivalentDomain = {
  domains: string;
  excluded: boolean;
  key: number;
};

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: "app-domain-rules",
  templateUrl: "domain-rules.component.html",
  imports: [SharedModule, HeaderModule, PopoverModule],
})
export class DomainRulesComponent implements OnInit {
  private readonly configService = inject(ConfigService);
  private readonly dialogService = inject(DialogService);

  protected readonly btnTextAddCreateFeatureFlag = toSignal(
    this.configService.getFeatureFlag$(FeatureFlag.PM32380_BtnTextAddCreate),
  );

  loading = true;
  custom: string[] = [];
  global: GlobalEquivalentDomain[] = [];
  openMenuKey: number | null = null;

  constructor(
    private apiService: ApiService,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private logService: LogService,
  ) {}

  async ngOnInit() {
    const response = await this.apiService.getSettingsDomains();
    this.loading = false;
    if (response.equivalentDomains != null) {
      this.custom = response.equivalentDomains.map((d) => d.join(", "));
    }
    if (response.globalEquivalentDomains != null) {
      this.global = response.globalEquivalentDomains.map((d) => {
        return {
          domains: d.domains.join(", "),
          excluded: d.excluded,
          key: d.type,
        };
      });
    }
  }

  closeMenu(): void {
    this.openMenuKey = null;
  }

  setMenuOpen(key: number, open: boolean): void {
    this.openMenuKey = open ? key : null;
  }

  isMenuOpen(key: number): boolean {
    return this.openMenuKey === key;
  }

  async openAddCustomDomainDialog(): Promise<void> {
    const dialogRef = AddCustomDomainDialogComponent.open(this.dialogService, {
      domains: [...this.custom],
    });
    const result = await firstValueFrom(dialogRef.closed);
    if (result == null) {
      return;
    }

    this.custom = result as AddCustomDomainDialogResult;
    await this.persistDomains();
  }

  async toggleExcluded(globalDomain: GlobalEquivalentDomain): Promise<void> {
    globalDomain.excluded = !globalDomain.excluded;
    this.closeMenu();
    await this.persistDomains();
  }

  async openCustomizeDialog(globalDomain: GlobalEquivalentDomain): Promise<void> {
    this.closeMenu();

    const dialogRef = CustomizeGlobalDomainDialogComponent.open(this.dialogService, {
      domains: globalDomain.domains,
    });
    const result = (await firstValueFrom(dialogRef.closed)) as
      | CustomizeGlobalDomainDialogResult
      | undefined;

    if (result == null) {
      return;
    }

    globalDomain.excluded = true;

    if (result.action === "save" && result.domains.trim() !== "") {
      this.custom.push(result.domains);
    }

    await this.persistDomains();
  }

  private async persistDomains(): Promise<void> {
    const request = new UpdateDomainsRequest();
    request.excludedGlobalEquivalentDomains = this.global
      .filter((d) => d.excluded)
      .map((d) => d.key);
    if (request.excludedGlobalEquivalentDomains.length === 0) {
      request.excludedGlobalEquivalentDomains = null;
    }
    request.equivalentDomains = this.custom
      .filter((d) => d != null && d.trim() !== "")
      .map((d) => d.split(",").map((d2) => d2.trim()));
    if (request.equivalentDomains.length === 0) {
      request.equivalentDomains = null;
    }

    await this.apiService.putSettingsDomains(request);
    this.platformUtilsService.showToast("success", null, this.i18nService.t("domainsUpdated"));
  }
}
